import { useQuery } from '@tanstack/react-query';

import { api } from './api';

/**
 * Money against projects.
 *
 * Every amount is minor units as a string: BigInt does not survive JSON, and a
 * silent Number() is how a money column starts losing paise. Nothing in the
 * browser does arithmetic on these - the API does every sum, and these are
 * formatted for display only.
 */
export interface ProjectBudget {
  /** 'month' resets each month; 'total' is for the life of the project. */
  type: 'month' | 'total';
  budgetMinor: string;
  timeBudgetSeconds: number;
  /** Over the budget's OWN period, which is not the filter range. */
  spentMinor: string;
  spentSeconds: number;
}

export interface ProjectMoneyRow {
  id: string;
  name: string;
  number: string | null;
  visible: boolean;
  customer: { id: string; name: string; currency: string };
  seconds: number;
  earnedMinor: string;
  costMinor: string;
  marginMinor: string;
  invoicedMinor: string;
  invoicedSeconds: number;
  /** Billed outside the timesheet - the previous system's partial billing. */
  billedOutsideMinor: string;
  uninvoicedMinor: string;
  budget: ProjectBudget | null;
}

export interface ProjectMoneyTotals {
  seconds: number;
  earnedMinor: string;
  costMinor: string;
  marginMinor: string;
  invoicedMinor: string;
  billedOutsideMinor: string;
  uninvoicedMinor: string;
}

export interface ProjectMoney {
  from: string;
  to: string;
  projects: ProjectMoneyRow[];
  totals: ProjectMoneyTotals;
}

export interface MoneyLine {
  id: string;
  name: string;
  seconds: number;
  earnedMinor: string;
  costMinor: string;
}

export interface ProjectMoneyDetail {
  project: { id: string; name: string; customer: { name: string; currency: string } };
  from: string;
  to: string;
  byActivity: MoneyLine[];
  byPerson: MoneyLine[];
  billings: { id: string; amountMinor: string; billedOn: string; comment: string | null }[];
  invoices: {
    id: string;
    number: string;
    status: string;
    issuedOn: string;
    grossMinor: string;
    currency: string;
  }[];
}

export const useProjectMoney = (from: string, to: string, includeHidden: boolean) =>
  useQuery({
    queryKey: ['reporting', 'projects', 'money', from, to, includeHidden],
    queryFn: () =>
      api.get<ProjectMoney>(
        `/reporting/projects/money?from=${from}&to=${to}&includeHidden=${includeHidden}`,
      ),
  });

/** Only fetched once a row is opened - it is a second set of scans per project. */
export const useProjectMoneyDetail = (id: string | null, from: string, to: string) =>
  useQuery({
    queryKey: ['reporting', 'projects', 'money', id, from, to],
    queryFn: () => api.get<ProjectMoneyDetail>(`/reporting/projects/${id}/money?from=${from}&to=${to}`),
    enabled: id !== null,
  });
